import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { Paperclip, X, FileText, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * File uploader with drag-drop-style button.
 * Props:
 *   value: [{file_id, original_filename, size, content_type}]
 *   onChange: fn(newList)
 *   purpose: "attachment" | "kyc" | "rfp"
 *   accept: input accept string
 *   testid: base data-testid
 */
export default function FileUploader({ value = [], onChange, purpose = "attachment", accept = ".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt", testid = "file-uploader" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const pickFiles = () => ref.current?.click();

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    const uploaded = [];
    for (const f of Array.from(files)) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 25 MB cap`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/uploads?purpose=${purpose}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    if (uploaded.length) onChange([...(value || []), ...uploaded]);
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };

  const remove = (fid) => {
    onChange((value || []).filter((v) => v.file_id !== fid));
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <input
        ref={ref}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={pickFiles}
        disabled={busy}
        className="btn-ghost text-sm w-full justify-center border-dashed"
        data-testid={`${testid}-trigger`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        {busy ? "Uploading…" : "Attach files (PDF, DOCX, images · max 25 MB)"}
      </button>
      {(value || []).length > 0 && (
        <ul className="space-y-1.5 mt-2">
          {value.map((f) => (
            <li key={f.file_id} className="flex items-center justify-between gap-3 text-sm bg-ledger border border-hairline rounded px-3 py-2" data-testid={`file-${f.file_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-brand flex-shrink-0" />
                <span className="truncate text-ink" title={f.original_filename}>{f.original_filename}</span>
                <span className="text-xs text-muted font-mono flex-shrink-0">{Math.round((f.size || 0) / 1024)} KB</span>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${f.file_id}?auth=${token || ""}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted hover:text-ink p-1"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  type="button"
                  onClick={() => remove(f.file_id)}
                  className="text-muted hover:text-red-600 p-1"
                  data-testid={`remove-file-${f.file_id}`}
                  title="Remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
